import dayjs from 'dayjs';
import { SwiperSlide } from 'swiper/react';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ChartHeader from 'components/charts/ChartHeader';
import DateFilter from 'components/field/DateFilter';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Skeleton from 'components/global/Skeleton';
import SwiperFooter from 'components/global/SwiperFooter';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';
import { FilterAction, FilterType } from 'components/list/DynamicListFilterUtils';

import useFetchAndParseOnView from 'hooks/useFetchAndParseOnView';
import useMediaQuery from 'hooks/useMediaQuery';

import { routeMapping } from 'pages/Shipments/utils/shipmentStatusMapping';

import { REPORT } from 'services/consts';

import { Breakpoints } from 'utils/const.styles';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

import { SectionWrapper } from '../OnboardingV2/Onboarding.styled';
import * as S from './QuickOverview.styled';

const QuickOverview = () => {
	const [swiperRef, setSwiperRef] = useState(null);
	const [filter, setFilter] = useState({
		start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter]);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.DASHBOARD.GET_DASHBOARD_SHIPMENT_NUMBERS,
		body
	});

	const {
		ref: codRef,
		data: codData,
		loading: codLoading
	} = useFetchAndParseOnView({
		url: REPORT.DASHBOARD.GET_DASHBOARD_COD_SUMMARY,
		body
	});

	const navigate = useNavigate();

	const {
		localization: { dir }
	} = useSelector((state) => state.layout);

	const handleButtonClick = (cardName, Header, accessor, extraFilters) => {
		navigate(routeMapping[cardName], {
			state: {
				defaultFilters: [
					{
						column: {
							Header,
							accessor,
							filterType: FilterType.date
						},
						action: FilterAction.between,
						...(body && {
							value: {
								start: body.from,
								end: body.to
							}
						})
					},
					...(extraFilters || [])
				]
			}
		});
	};

	const isMobile = useMediaQuery(Breakpoints.mobile);

	const dataCardConfigs = [
		{
			accessor: 'pending',
			label: 'pending',
			onButtonClick: () => {
				handleButtonClick('pending', 'orderDate', 'createdDate');
			},
			imageUrl:
				'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/quickOverviewCards/awaitingPickup.svg'
		},
		{
			accessor: 'awaitingPickup',
			label: 'awaitingPickup',
			imageUrl:
				'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/quickOverviewCards/pendingOrders.svg',
			onButtonClick: () => {
				handleButtonClick('awaitingPickup', 'shipmentCreationDate', 'shipmentCreationDate');
			}
		},
		{
			accessor: 'currentlyShipping',
			label: 'currentlyShipping',
			imageUrl:
				'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/quickOverviewCards/currentlyShipping.svg',
			onButtonClick: () => {
				handleButtonClick('currentlyShipping', 'orderDate', 'createdDate');
			}
		},
		{
			accessor: 'delivered',
			label: 'delivered',
			imageUrl:
				'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/quickOverviewCards/deliverySuccessRate.svg',
			onButtonClick: () => {
				handleButtonClick('delivered', 'deliveryDate', 'deliveryDate', [
					{
						column: {
							Header: 'orderDate',
							accessor: 'createdDate',
							filterType: FilterType.date
						},
						action: FilterAction.notEmpty
					}
				]);
			}
		},
		{
			accessor: 'returned',
			label: 'returned',
			imageUrl:
				'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/quickOverviewCards/returnRate.svg',
			onButtonClick: () => {
				handleButtonClick('returned', 'returnDate', 'returnDate');
			}
		},
		{
			accessor: 'CODAmount',
			label: 'dashboard.statistic.totalCodAmount',
			withCurrency: true,
			imageUrl:
				'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/quickOverviewCards/codAmount.svg'
		}
	];

	return (
		<SectionWrapper ref={ref}>
			<ChartHeader
				title={
					<S.Header>
						<Typography variant="p2" fontWeight="700">
							quickOverview
						</Typography>
						<Tooltip placement="right" title={'quickOverviewTooltip'}>
							<Icon icon={faCircleInfo} />
						</Tooltip>
					</S.Header>
				}
				filter={<DateFilter defaultDay={'last30Days'} setValue={setFilter} />}
				showDivider={false}
			/>

			{loading ? (
				<Skeleton width="100%" height="10rem" />
			) : (
				<S.SwiperContainer
					key={`quick-overview-${dir}`}
					dir={dir}
					ref={codRef}
					slidesPerView={isMobile ? 2 : 3}
					onSwiper={setSwiperRef}>
					{dataCardConfigs.slice(0, 5).map((config, index) => {
						return (
							<SwiperSlide key={index}>
								{data?.[0][config?.accessor] !== undefined && (
									<StyledDataCard
										config={config}
										data={data?.[0][config?.accessor]}
										loading={loading}
									/>
								)}
							</SwiperSlide>
						);
					})}
					<SwiperSlide key={5}>
						{dataCardConfigs[5] && codData && (
							<StyledDataCard
								config={dataCardConfigs[5]}
								data={codData.value}
								loading={codLoading}
								currency={codData.currency}
							/>
						)}
					</SwiperSlide>
					{swiperRef?.slides?.length > 0 && !loading && <SwiperFooter swiperRef={swiperRef} />}
				</S.SwiperContainer>
			)}
		</SectionWrapper>
	);
};

export default QuickOverview;

const StyledDataCard = ({ config, data, loading, currency }) => {
	const { t } = useTranslation();
	return (
		<S.StyledDataCard onClick={config.onButtonClick}>
			<Image url={config.imageUrl} alt={config.label} width="6rem" height="6rem" />
			<S.TextWrapper>
				<Typography
					noWrap
					gutterBottom
					variant="caption"
					color="var(--content-3)"
					fontWeight="700"
					autoTranslation={false}>
					{t(config.label).toUpperCase()}
				</Typography>
				{loading ? (
					<Skeleton width="5rem" height="2.2rem" />
				) : (
					<Typography variant="h4" fontWeight="700" withCurrency={config.withCurrency} currency={currency}>
						{data}
					</Typography>
				)}
			</S.TextWrapper>
		</S.StyledDataCard>
	);
};
